import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Trophy, Flame, ShieldAlert, Swords, Check, ArrowRight, Mail } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { toast } from "sonner";
import { signInGoogle, signInApple } from "@/lib/auth/authClient";
import { TurnstileGate } from "@/components/security/TurnstileGate";
import {
  PENDING_XP_AMOUNT,
  suggestedNationLabel,
  type GuestPredictionChoice,
} from "@/lib/predictions/guestPrediction";

const VARIANT_KEY = "vs_claim_xp_variant";
const OAUTH_VARIANT_KEY = "vs_claim_xp_oauth_variant";
const HOLD_SECONDS = 10 * 60; // 10:00 countdown — predicción conservada

function getVariant(): "A" | "B" {
  if (typeof window === "undefined") return "A";
  try {
    const existing = window.localStorage.getItem(VARIANT_KEY);
    if (existing === "A" || existing === "B") return existing;
    const v: "A" | "B" = Math.random() < 0.5 ? "A" : "B";
    window.localStorage.setItem(VARIANT_KEY, v);
    void trackEvent("claim_xp_variant_assigned", { variant: v });
    return v;
  } catch {
    return "A";
  }
}

// PR-32 — OAuth ordering A/B:
//   A = Google primero (Apple secundario)
//   B = Apple/Google mismo peso (lado a lado)
function getOauthVariant(): "A" | "B" {
  if (typeof window === "undefined") return "A";
  try {
    const existing = window.localStorage.getItem(OAUTH_VARIANT_KEY);
    if (existing === "A" || existing === "B") return existing;
    const v: "A" | "B" = Math.random() < 0.5 ? "A" : "B";
    window.localStorage.setItem(OAUTH_VARIANT_KEY, v);
    void trackEvent("claim_xp_oauth_variant_assigned", { variant: v });
    return v;
  } catch {
    return "A";
  }
}

function format(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
}

function useDynamicClaimCount() {
  // Stable but dynamic-looking number, seeded by hour-of-day. Avoids
  // any backend call and never lies about totals — clearly framed as
  // "hoy" on the UI side.
  return useMemo(() => {
    const d = new Date();
    const base = 2800 + d.getHours() * 47 + d.getMinutes();
    return base.toLocaleString("es-MX");
  }, []);
}

interface Props {
  choice: GuestPredictionChoice;
  suggestedNation: string | null;
  isAuthenticated: boolean;
}

/**
 * PR-31 — Claim XP Conversion Engine.
 *
 * Premium intermediate result screen shown after a guest finishes the
 * micro-prediction. Adds urgency (countdown), loss aversion, a single
 * dominant CTA, micro-benefits, social proof and a friend-challenge
 * teaser. Fires the PR-31 PostHog funnel without touching XP, Nation
 * Wars, Streaks, Sponsors or the Prediction Engine.
 */
export function ClaimXpResultScreen({ choice, suggestedNation, isAuthenticated }: Props) {
  const nation = suggestedNationLabel(suggestedNation);
  const claimsToday = useDynamicClaimCount();
  const [remaining, setRemaining] = useState(HOLD_SECONDS);
  const [variant] = useState<"A" | "B">(() => getVariant());
  const [oauthVariant] = useState<"A" | "B">(() => getOauthVariant());
  const [oauthLoading, setOauthLoading] = useState<"google" | "apple" | null>(null);
  const abandonFired = useRef(false);

  useEffect(() => {
    void trackEvent("guest_prediction_result_view", {
      choice,
      variant,
      suggested_nation: suggestedNation,
    });
    void trackEvent("claim_xp_cta_view", { variant, choice });
  }, [choice, suggestedNation, variant]);

  useEffect(() => {
    if (isAuthenticated) return;
    const id = window.setInterval(() => {
      setRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const onLeave = () => {
      if (abandonFired.current) return;
      abandonFired.current = true;
      void trackEvent("claim_xp_abandoned", {
        variant,
        seconds_left: remaining,
      });
    };
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pagehide", onLeave);
    };
  }, [isAuthenticated, variant, remaining]);

  const headlineA = `+${PENDING_XP_AMOUNT} XP pendientes`;
  const headlineB = "Tu predicción está esperando ser guardada";
  const headline = variant === "A" ? headlineA : headlineB;

  const onClaim = () => {
    void trackEvent("claim_xp_cta_click", { variant, choice });
    void trackEvent("claim_xp_signup_started", { variant, choice });
  };

  const startOauth = async (provider: "google" | "apple") => {
    if (oauthLoading) return;
    setOauthLoading(provider);
    void trackEvent("oauth_claim_started", {
      provider,
      variant,
      oauth_variant: oauthVariant,
      choice,
    });
    void trackEvent("claim_xp_cta_click", { variant, choice, provider });
    void trackEvent("claim_xp_signup_started", { variant, choice, provider });
    try {
      const res = provider === "google" ? await signInGoogle() : await signInApple();
      // If the SDK returned an error object without throwing.
      const err = (res as { error?: { message?: string } } | undefined)?.error;
      if (err) {
        void trackEvent("oauth_claim_failed", { provider, reason: err.message ?? "unknown" });
        toast.error(err.message ?? "No pudimos iniciar sesión");
        setOauthLoading(null);
      }
      // On success the browser navigates to the OAuth provider; the
      // callback page completes claimPendingPrediction() automatically.
    } catch (e) {
      const message = e instanceof Error ? e.message : "unknown";
      void trackEvent("oauth_claim_failed", { provider, reason: message });
      toast.error("No pudimos iniciar sesión con " + (provider === "google" ? "Google" : "Apple"));
      setOauthLoading(null);
    }
  };

  const GoogleBtn = (
    <button
      type="button"
      disabled={!!oauthLoading}
      onClick={() => void startOauth("google")}
      className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-base font-black uppercase tracking-wide text-primary-foreground shadow-lg transition active:scale-[0.98] disabled:opacity-60"
    >
      {oauthLoading === "google" ? "..." : <>🏆 Continuar con Google <ArrowRight className="h-4 w-4" /></>}
    </button>
  );
  const AppleBtn = (
    <button
      type="button"
      disabled={!!oauthLoading}
      onClick={() => void startOauth("apple")}
      className="flex w-full items-center justify-center gap-2 rounded-xl border border-foreground/30 bg-foreground px-4 py-3 text-sm font-black uppercase tracking-wide text-background shadow transition active:scale-[0.98] disabled:opacity-60"
    >
      {oauthLoading === "apple" ? "..." : <> Continuar con Apple</>}
    </button>
  );

  return (
    <section
      aria-label="Resultado de tu predicción"
      className="overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-4 shadow-xl"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 items-center gap-1 rounded-full bg-primary px-2 text-[10px] font-black uppercase tracking-widest text-primary-foreground">
          <Flame className="h-3 w-3" /> Predicción registrada
        </span>
        {nation && (
          <span className="ml-auto text-[11px] font-bold text-muted-foreground">
            🌎 {nation.flag} {nation.name}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Trophy className="h-5 w-5 text-amber-500" />
        <h2 className="text-lg font-black leading-tight text-foreground">{headline}</h2>
      </div>

      {!isAuthenticated && (
        <div className="mt-3 rounded-xl border border-border bg-background/60 p-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Tu predicción será conservada durante
          </p>
          <p
            className="mt-1 font-mono text-3xl font-black tabular-nums text-primary"
            aria-live="polite"
          >
            {format(remaining)}
          </p>
        </div>
      )}

      <div className="mt-3 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3">
        <div className="flex items-center gap-2 text-[12px] font-black text-amber-500">
          <ShieldAlert className="h-4 w-4" /> Si abandonas ahora perderás:
        </div>
        <ul className="mt-1 space-y-0.5 pl-6 text-[11px] text-muted-foreground">
          <li>• Tu predicción</li>
          <li>• Tus XP pendientes</li>
          <li>• Tu progreso inicial</li>
        </ul>
      </div>

      {!isAuthenticated ? (
        <div className="mt-4 space-y-2">
          {oauthVariant === "A" ? (
            <>
              {GoogleBtn}
              {AppleBtn}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {GoogleBtn}
              {AppleBtn}
            </div>
          )}
          <Link
            to="/registro"
            onClick={onClaim}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-border bg-background/60 px-4 py-2.5 text-xs font-bold text-muted-foreground hover:text-foreground"
          >
            <Mail className="h-3.5 w-3.5" /> Usar email tradicional
          </Link>
          <TurnstileGate action="claim_xp" onVerified={() => undefined} />
        </div>
      ) : (
        <Link
          to="/quinielas"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-base font-black uppercase tracking-wide text-primary-foreground shadow-lg active:scale-[0.98]"
        >
          🏆 Ir a predicciones reales
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}

      <ul className="mt-3 grid grid-cols-1 gap-1 text-[11px] text-foreground/80">
        {[
          "Guardar predicción",
          "Entrar a Nation Wars",
          "Activar racha diaria",
          "Desafiar amigos",
          "Subir en rankings",
        ].map((b) => (
          <li key={b} className="flex items-center gap-1.5">
            <Check className="h-3 w-3 text-primary" /> {b}
          </li>
        ))}
      </ul>

      <p className="mt-3 text-center text-[11px] font-bold text-muted-foreground">
        {claimsToday} usuarios ya reclamaron sus XP hoy
      </p>

      <div className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-border bg-background/40 px-3 py-2 text-[11px] text-muted-foreground">
        <Swords className="h-3.5 w-3.5 text-primary" />
        <span>
          <span className="font-black text-foreground">⚔️ Próximamente</span> podrás retar a tus amigos · disponible tras reclamar XP
        </span>
      </div>
    </section>
  );
}